import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../services/HttpManager";
import { MentorPayMonthDTO } from "../../services/v2/workshops.service";
import "./pagoMaestra.css";

interface Props {
	campusId: number;
	userId: number;
	nombre: string;
	soloLectura?: boolean;
	onClose: () => void;
}

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const horas = (h: number) => `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
const errorDe = (e: any): string => e?.response?.data?.message || "No se pudo guardar.";

/**
 * Pago de Maestra (Esteban, 2026-10-23), desde su ficha en Personal: de planta (salario fijo al mes que cubre hasta un máximo
 * de horas de clase; lo que pasa del tope se paga a su tarifa por hora) o por hora. Talleres lo usa para calcular su pago.
 */
const PagoMaestraModal: React.FC<Props> = ({ campusId, userId, nombre, soloLectura, onClose }) => {
	const http = HttpManager.getInstance();
	const [tipo, setTipo] = useState<"planta" | "hora">("hora");
	const [salario, setSalario] = useState<string>("");
	const [tope, setTope] = useState<string>("");
	const [tarifa, setTarifa] = useState<string>("");
	const [moneda, setMoneda] = useState<string>("");
	const [mes, setMes] = useState<MentorPayMonthDTO | null>(null);
	const [cargado, setCargado] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<boolean>(false);

	useEffect(() => {
		http.workshopsService.mentorPay(campusId, userId)
			.then(r => {
				const c = r.data.config;
				if (c) {
					setTipo(c.payType); setSalario(c.monthlySalary != null ? String(c.monthlySalary) : ""); setTope(c.monthlyHoursCap != null ? String(c.monthlyHoursCap) : "");
					setTarifa(c.hourlyRate != null ? String(c.hourlyRate) : ""); setMoneda(c.currency?.code || "");
				}
				setMes(r.data.month);
			})
			.catch(e => setError(errorDe(e)))
			.finally(() => setCargado(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, userId]);

	const num = (v: string) => (v.trim() === "" || isNaN(Number(v)) ? null : Number(v));
	const valido = tipo === "hora" ? (num(tarifa) || 0) > 0 : (num(salario) || 0) > 0 && (num(tope) || 0) > 0 && Number.isInteger(num(tope));
	const guardar = () => {
		setBusy(true); setError(null); setOk(false);
		// Por hora no lleva salario ni tope: no se mandan aunque se hayan escrito antes de cambiar
		http.workshopsService.saveMentorPay(campusId, userId, { payType: tipo, monthlySalary: tipo === "planta" ? num(salario) : null,
			monthlyHoursCap: tipo === "planta" ? num(tope) : null, hourlyRate: num(tarifa) })
			.then(r => { setMes(r.data.month); setMoneda(r.data.config.currency?.code || moneda); setOk(true); })
			.catch(e => setError(errorDe(e)))
			.finally(() => setBusy(false));
	};
	const conMoneda = (t: string) => moneda ? `${t} (${moneda})` : t;
	const nombreMes = mes ? MESES[Number(mes.month.substring(5, 7)) - 1] : "";

	return (
		<Modal show onHide={onClose} centered className="pm-modal" aria-labelledby="pm-titulo">
			<Modal.Header closeButton closeLabel="Cerrar">
				<Modal.Title id="pm-titulo">Pago de Maestra <span className="pm-nombre">{nombre}</span></Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{!cargado ? <div className="pm-gris">Cargando…</div> : <>
					<div className="pm-seg" role="radiogroup" aria-label="Forma de pago">
						<button type="button" role="radio" aria-checked={tipo === "planta"} disabled={soloLectura} onClick={() => { setTipo("planta"); setOk(false); }}>De Planta</button>
						<button type="button" role="radio" aria-checked={tipo === "hora"} disabled={soloLectura} onClick={() => { setTipo("hora"); setOk(false); }}>Por Hora</button>
					</div>
					<div className="pm-campos">
						{tipo === "planta" ? <>
							<label className="pm-campo"><span>{conMoneda("Salario Mensual")}</span>
								<input type="number" min={0} step="0.01" value={salario} disabled={soloLectura} onChange={e => { setSalario(e.target.value); setOk(false); }} /></label>
							<label className="pm-campo"><span>Máximo de Horas al Mes</span>
								<input type="number" min={1} step="1" value={tope} disabled={soloLectura} onChange={e => { setTope(e.target.value); setOk(false); }} /></label>
						</> : null}
						<label className="pm-campo"><span>{conMoneda(tipo === "planta" ? "Tarifa por Hora Extra" : "Tarifa por Hora")}</span>
							<input type="number" min={0} step="0.01" value={tarifa} disabled={soloLectura} onChange={e => { setTarifa(e.target.value); setOk(false); }} /></label>
					</div>
					{mes ? (
						<div className="pm-mes">
							<span className="pm-lab">Horas de Clase en {nombreMes}</span>
							<b className={"pm-num" + (mes.extraHours ? " oro" : "")}>{horas(mes.hours)}{mes.cap ? ` de ${mes.cap}` : ""}</b>
							{mes.extraHours ? <span className="pm-oro">{horas(mes.extraHours)} extra</span> : null}
						</div>
					) : null}
					{error ? <p className="pm-oro" role="alert">{error}</p> : null}
					{ok ? <p className="pm-ok" role="status">Guardado.</p> : null}
				</>}
			</Modal.Body>
			{soloLectura ? null : (
				<Modal.Footer>
					<button type="button" className="btn btn-primary" disabled={busy || !valido || !cargado} onClick={guardar}>Guardar</button>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default PagoMaestraModal;
